import React from "react"
import { isValidDatestamp, useCreateHardEligibilitySession } from "@usebridge/sdk-react"

import type { InsuranceMember, InsuranceInputs, InsuranceEvent } from "./insuranceTypes"
export type {
  InsuranceMember,
  InsuranceInputs,
  InsuranceEvent,
  InsuranceCheckInputs,
  InsuranceResult,
  InsuranceError
} from "./insuranceTypes"

type Session = ReturnType<ReturnType<typeof useCreateHardEligibilitySession>>

// Sessions and patient details stay in memory. Never forward SDK errors or payloads to analytics.
export function useInsuranceCheck(
  member: InsuranceMember | undefined,
  serviceTypeIds: string[],
  onEvent: (event: InsuranceEvent) => void
) {
  const createSession = useCreateHardEligibilitySession()
  const activeRequest = React.useRef<string | null>(null)
  const configurationKey = JSON.stringify([member, serviceTypeIds])
  React.useLayoutEffect(() => {
    activeRequest.current = null
    onEvent({ type: "INSURANCE_INVALIDATED" })
    return () => {
      activeRequest.current = null
    }
  }, [configurationKey, onEvent])

  const invalidate = () => {
    activeRequest.current = null
  }
  const memberReady = Boolean(
    member?.firstName.trim() && member?.lastName.trim() && isValidDatestamp(member?.dateOfBirth)
  )
  const submit = async (inputs: InsuranceInputs) => {
    if (activeRequest.current || !memberReady || !member || !serviceTypeIds.length) return
    const requestId = crypto.randomUUID()
    activeRequest.current = requestId
    onEvent({
      type: "INSURANCE_STARTED",
      requestId,
      inputs: { ...inputs, member, serviceTypeIds: [...serviceTypeIds] }
    })
    try {
      // A fresh session isolates retries and edits from the SDK's previous polling/result state.
      const session = createSession({ serviceTypeIds })
      const [year, month, day] = member.dateOfBirth.split("-")
      const result = await session.submit({
        state: inputs.state as Parameters<Session["submit"]>[0]["state"],
        patient: {
          payerId: inputs.payerId,
          firstName: member.firstName,
          lastName: member.lastName,
          dateOfBirth: { year, month, day },
          memberId: inputs.memberId
        }
      })
      if (activeRequest.current !== requestId) return
      if (result.status === "ELIGIBLE" && !result.policy?.id?.trim()) {
        onEvent({ type: "INSURANCE_FAILED", requestId, error: { code: "SERVER_ERROR", retryable: true } })
        return
      }
      if (result.status === "ELIGIBLE" || result.status === "INELIGIBLE") {
        onEvent({
          type: "INSURANCE_RESULT",
          requestId,
          result: {
            status: result.status,
            policyId: result.policy?.id,
            providers: result.providers ?? [],
            patientResponsibility: result.patientResponsibility,
            serviceEligibility: result.serviceEligibility,
            ineligibilityReason: result.ineligibilityReason
          }
        })
      } else {
        onEvent({
          type: "INSURANCE_FAILED",
          requestId,
          error: {
            code: result.error?.code ?? result.status,
            retryable: result.error?.retryable ?? true
          }
        })
      }
    } catch {
      if (activeRequest.current === requestId) {
        onEvent({ type: "INSURANCE_FAILED", requestId, error: { code: "SERVER_ERROR", retryable: true } })
      }
    } finally {
      if (activeRequest.current === requestId) activeRequest.current = null
    }
  }
  return { submit, invalidate, memberReady }
}
